// Numeric field with three input paths, all clamped and rounded identically:
//  - type, commit on blur or Enter (never per keystroke)
//  - scrub: drag the label horizontally (Shift = coarse x10, Alt = fine x0.1)
//  - tiny stepper arrows beside the value, one unit per click
// A scrub runs as one undo gesture when live callbacks are wired.

import { useEffect, useRef, useState } from 'react'
import type { DocumentModel } from '../core/types'
import { useStore } from '../state/store'
import { IconChevronDown } from './icons'

export interface FieldOps {
  onCommit: (v: number) => void
  onLive?: (v: number) => void
  onGestureStart?: () => void
  onGestureEnd?: () => void
}

interface Props extends FieldOps {
  label?: string
  value: number
  min?: number
  max?: number
  step?: number
  unit?: string
  compact?: boolean
  title?: string
}

// Binds a field to a document mutation with a labeled undo gesture.
export function useFieldOps(label: string, apply: (d: DocumentModel, v: number) => void): FieldOps {
  const commit = useStore((s) => s.commit)
  const live = useStore((s) => s.live)
  const beginGesture = useStore((s) => s.beginGesture)
  const endGesture = useStore((s) => s.endGesture)
  return {
    onCommit: (v) => commit(label, (d) => apply(d, v)),
    onLive: (v) => live((d) => apply(d, v)),
    onGestureStart: () => beginGesture(label),
    onGestureEnd: endGesture,
  }
}

function round3(v: number): number {
  return Math.round(v * 1000) / 1000
}

function niceStep(min?: number, max?: number): number {
  if (min === undefined || max === undefined) return 1
  const raw = (max - min) / 300
  const mag = Math.pow(10, Math.floor(Math.log10(Math.max(raw, 1e-6))))
  for (const m of [1, 2, 5, 10]) {
    if (mag * m >= raw) return mag * m
  }
  return mag * 10
}

export function ValueField({
  label,
  value,
  min,
  max,
  step,
  unit,
  compact,
  title,
  onCommit,
  onLive,
  onGestureStart,
  onGestureEnd,
}: Props) {
  const [text, setText] = useState(String(round3(value)))
  const [scrubbing, setScrubbing] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const baseStep = step ?? niceStep(min, max)

  useEffect(() => setText(String(round3(value))), [value])

  const clamp = (v: number) => {
    if (min !== undefined) v = Math.max(min, v)
    if (max !== undefined) v = Math.min(max, v)
    return round3(v)
  }

  const commitText = () => {
    const v = parseFloat(text)
    if (!Number.isFinite(v)) {
      setText(String(round3(value)))
      return
    }
    const c = clamp(v)
    setText(String(c))
    if (c !== value) onCommit(c)
  }

  const modStep = (e: { shiftKey: boolean; altKey: boolean }) =>
    baseStep * (e.shiftKey ? 10 : e.altKey ? 0.1 : 1)

  // Label scrub: one horizontal drag = one undo step.
  const onLabelPointerDown = (e: React.PointerEvent<HTMLSpanElement>) => {
    if (e.button !== 0) return
    e.preventDefault()
    const el = e.currentTarget
    el.setPointerCapture(e.pointerId)
    setScrubbing(true)
    const startX = e.clientX
    const startValue = value
    let last = value
    onGestureStart?.()

    const onMove = (ev: PointerEvent) => {
      const dx = ev.clientX - startX
      const v = clamp(startValue + dx * modStep(ev))
      if (v === last) return
      last = v
      if (onLive) onLive(v)
    }
    const onUp = () => {
      el.removeEventListener('pointermove', onMove)
      el.removeEventListener('pointerup', onUp)
      el.removeEventListener('pointercancel', onUp)
      setScrubbing(false)
      if (onLive) onGestureEnd?.()
      else if (last !== startValue) onCommit(last)
    }
    el.addEventListener('pointermove', onMove)
    el.addEventListener('pointerup', onUp)
    el.addEventListener('pointercancel', onUp)
  }

  // Stepper arrows: exactly one unit per click, each click one undo step.
  const nudge = (dir: 1 | -1) => {
    const v = clamp(value + dir)
    if (v !== value) onCommit(v)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      ;(e.target as HTMLInputElement).blur()
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault()
      const dir = e.key === 'ArrowUp' ? 1 : -1
      const v = clamp(value + dir * modStep(e))
      if (v !== value) onCommit(v)
    }
  }

  return (
    <label className={compact ? 'vfield compact' : 'vfield'} title={title}>
      {label && (
        <span
          className={scrubbing ? 'vfield-label scrubbing' : 'vfield-label'}
          onPointerDown={onLabelPointerDown}
          title={title ?? 'Drag to adjust. Shift: coarse, Alt: fine'}
        >
          {label}
        </span>
      )}
      <span className="vfield-box">
        <input
          ref={inputRef}
          type="text"
          inputMode="decimal"
          value={text}
          aria-label={label}
          onChange={(e) => setText(e.target.value)}
          onBlur={commitText}
          onKeyDown={onKeyDown}
        />
        <span className="vfield-steppers">
          <button
            type="button"
            tabIndex={-1}
            className="vfield-step up"
            aria-label={label ? `Increase ${label}` : 'Increase value'}
            onClick={() => nudge(1)}
          >
            <IconChevronDown size={8} />
          </button>
          <button
            type="button"
            tabIndex={-1}
            className="vfield-step"
            aria-label={label ? `Decrease ${label}` : 'Decrease value'}
            onClick={() => nudge(-1)}
          >
            <IconChevronDown size={8} />
          </button>
        </span>
      </span>
      {unit && <span className="vfield-unit">{unit}</span>}
    </label>
  )
}

interface SliderProps {
  label: string
  value: number
  min: number
  max: number
  step?: number
  unit?: string
  onLive: (v: number) => void
  onStart: () => void
  onEnd: () => void
}

// Slider with live preview during the drag; the gesture commits as one undo
// step on release. Clicking the value readout swaps it for a text input to
// type an exact value, committed on blur or Enter (Escape cancels).
const SLIDER_KEYS = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End']

export function SliderField({ label, value, min, max, step, unit, onLive, onStart, onEnd }: SliderProps) {
  const [editing, setEditing] = useState(false)
  const [text, setText] = useState('')
  const cancelRef = useRef(false)
  const editRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (editing) editRef.current?.select()
  }, [editing])

  const commitEdit = () => {
    setEditing(false)
    if (cancelRef.current) {
      cancelRef.current = false
      return
    }
    const v = parseFloat(text)
    if (!Number.isFinite(v)) return
    const c = Math.round(Math.min(max, Math.max(min, v)) * 1000) / 1000
    if (c === value) return
    onStart()
    onLive(c)
    onEnd()
  }

  return (
    <div className="sliderfield">
      <span className="row-label">{label}</span>
      <input
        type="range"
        value={value}
        min={min}
        max={max}
        step={step ?? 0.1}
        aria-label={label}
        onPointerDown={onStart}
        onPointerUp={onEnd}
        onKeyDown={(e) => SLIDER_KEYS.includes(e.key) && onStart()}
        onKeyUp={(e) => SLIDER_KEYS.includes(e.key) && onEnd()}
        onBlur={onEnd}
        onChange={(e) => onLive(parseFloat(e.target.value))}
      />
      {editing ? (
        <input
          ref={editRef}
          className="sliderfield-edit"
          type="text"
          inputMode="decimal"
          autoFocus
          value={text}
          aria-label={`${label} exact value`}
          onChange={(e) => setText(e.target.value)}
          onBlur={commitEdit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              ;(e.target as HTMLInputElement).blur()
            } else if (e.key === 'Escape') {
              e.stopPropagation()
              cancelRef.current = true
              ;(e.target as HTMLInputElement).blur()
            }
          }}
        />
      ) : (
        <button
          type="button"
          className="sliderfield-value"
          title="Click to type an exact value"
          onClick={() => {
            setText(String(Math.round(value * 1000) / 1000))
            setEditing(true)
          }}
        >
          {Math.round(value * 100) / 100}
          {unit ?? ''}
        </button>
      )}
    </div>
  )
}
